// VaultKe Web Theme - Re-export from mobile theme for consistency
// Note: Direct imports from outside src/ are not allowed in React apps
// So we define the theme utilities directly here, matching the mobile theme

// Colors
export const lightTheme = {
  // Primary colors
  primary: '#2563EB', // Blue
  primaryLight: '#3B82F6',
  primaryDark: '#1D4ED8',

  // Secondary colors
  secondary: '#059669', // Green
  secondaryLight: '#10B981',
  secondaryDark: '#047857',

  // Accent colors
  accent: '#DC2626', // Red
  accentLight: '#EF4444',
  accentDark: '#B91C1C',

  // Background colors
  background: '#F8FAFC',         // cool off-white
  backgroundSecondary: '#F1F5F9',
  backgroundTertiary: '#E2E8F0',  // muted layer (Slate 200)

  // Surface colors
  surface: '#F5F5F5',
  surfaceSecondary: '#F9FAFB',    // soft light gray for variation

  // Text colors
  text: '#0F172A',         // almost-black (Slate 900)
  textSecondary: '#334155', // darker gray-blue (Slate 700)
  textTertiary: '#64748B',  // medium gray (Slate 500)
  textInverse: '#FFFFFF',   // for dark surfaces

  // Border colors
  border: '#CBD5E1',        // light gray-blue (Slate 300)
  borderSecondary: '#E2E8F0',

  // Status colors
  success: '#059669',
  warning: '#D97706',
  error: '#DC2626',
  info: '#2563EB',

  // Utility colors
  white: '#FFFFFF',
  black: '#000000',
  transparent: 'transparent',

  // Shadow colors
  shadow: 'rgba(0, 0, 0, 0.05)',   // softer shadows
  shadowDark: 'rgba(0, 0, 0, 0.15)',
};

export const darkTheme = {
  // Primary colors
  primary: '#3B82F6', // Blue
  primaryLight: '#60A5FA',
  primaryDark: '#2563EB',

  // Secondary colors
  secondary: '#10B981', // Green
  secondaryLight: '#34D399',
  secondaryDark: '#059669',

  // Accent colors
  accent: '#EF4444', // Red
  accentLight: '#F87171',
  accentDark: '#DC2626',

  // Background colors
  background: '#081b29', // Slate 900
  backgroundSecondary: '#112e42', // Slate 800
  backgroundTertiary: '#334155', // Slate 700

  // Surface colors
  surface: '#112e42', // Slate 800
  surfaceSecondary: '#334155', // Slate 700

  // Text colors
  text: '#F8FAFC', // Slate 50
  textSecondary: '#CBD5E1', // Slate 300
  textTertiary: '#94A3B8', // Slate 400
  textInverse: '#0F172A', // Slate 900

  // Border colors
  border: '#475569', // Slate 600
  borderSecondary: '#64748B', // Slate 500

  // Status colors
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  info: '#3B82F6',

  // Utility colors
  white: '#FFFFFF',
  black: '#000000',
  transparent: 'transparent',

  // Shadow colors
  shadow: 'rgba(0, 0, 0, 0.3)',
  shadowDark: 'rgba(0, 0, 0, 0.5)',
};

// Get theme colors based on theme mode
export const getThemeColors = (themeMode = 'dark') => {
  return themeMode === 'dark' ? darkTheme : lightTheme;
};

// Common spacing values
export const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
  xxxl: 64,
};

// Common border radius values
export const borderRadius = {
  xs: 2,
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  xxl: 24,
  full: 9999,
};

// Typography styles
export const typography = {
  // Font sizes
  fontSize: {
    xs: 12,
    sm: 14,
    base: 16,
    lg: 18,
    xl: 20,
    '2xl': 24,
    '3xl': 30,
    '4xl': 36,
    '5xl': 48,
  },

  // Font weights
  fontWeight: {
    light: '300',
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
    extrabold: '800',
  },

  // Line heights
  lineHeight: {
    tight: 1.25,
    snug: 1.375,
    normal: 1.5,
    relaxed: 1.625,
    loose: 2,
  },
};

// Shadow styles - Updated to use boxShadow for web compatibility
export const shadows = {
  sm: {
    // Web: boxShadow format (preferred)
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    // Mobile: legacy shadow props for React Native (still needed for native)
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  md: {
    // Web: boxShadow format (preferred)
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    // Mobile: legacy shadow props for React Native (still needed for native)
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  lg: {
    // Web: boxShadow format (preferred)
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
    // Mobile: legacy shadow props for React Native (still needed for native)
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
  },
  xl: {
    // Web: boxShadow format (preferred)
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
    // Mobile: legacy shadow props for React Native (still needed for native)
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 8,
    },
    shadowOpacity: 0.2,
    shadowRadius: 16,
    elevation: 8,
  },
};

// Helper function to get platform-appropriate shadow styles
export const getShadowStyle = (shadowLevel: keyof typeof shadows = 'md') => {
  const shadowConfig = shadows[shadowLevel] || shadows.md;
  return {
    boxShadow: shadowConfig.boxShadow,
    elevation: shadowConfig.elevation,
  };
};

// Create VaultKeTheme object for backward compatibility
export const VaultKeTheme = {
  colors: {
    // Primary colors
    primary: '#00D4AA',
    primaryDark: '#00B894',
    primaryLight: '#55E6C1',

    // Background colors
    background: '#0D1117',
    surface: '#161B22',
    surfaceVariant: '#21262D',

    // Text colors
    text: '#F0F6FC',
    textSecondary: '#8B949E',
    textTertiary: '#6E7681',

    // Status colors
    success: '#238636',
    warning: '#D29922',
    error: '#DA3633',
    info: '#1F6FEB',

    // Border and divider colors
    border: '#30363D',
    divider: '#21262D',

    // Wallet specific colors
    walletBalance: '#00D4AA',
    walletIncome: '#238636',
    walletExpense: '#DA3633',

    // Chama specific colors
    chamaRole: {
      chairperson: '#D29922',
      treasurer: '#1F6FEB',
      secretary: '#8957E5',
      member: '#8B949E',
      assistant: '#6E7681',
    },

    // Chat colors
    chatBubbleSent: '#00D4AA',
    chatBubbleReceived: '#21262D',
    chatOnline: '#238636',
    chatOffline: '#6E7681',
  },

  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },

  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    round: '50px',
  },

  typography: {
    h1: {
      fontSize: '32px',
      fontWeight: 'bold',
      lineHeight: '40px',
    },
    h2: {
      fontSize: '28px',
      fontWeight: 'bold',
      lineHeight: '36px',
    },
    h3: {
      fontSize: '24px',
      fontWeight: '600',
      lineHeight: '32px',
    },
    h4: {
      fontSize: '20px',
      fontWeight: '600',
      lineHeight: '28px',
    },
    h5: {
      fontSize: '18px',
      fontWeight: '600',
      lineHeight: '24px',
    },
    h6: {
      fontSize: '16px',
      fontWeight: '600',
      lineHeight: '22px',
    },
    body1: {
      fontSize: '16px',
      fontWeight: 'normal',
      lineHeight: '24px',
    },
    body2: {
      fontSize: '14px',
      fontWeight: 'normal',
      lineHeight: '20px',
    },
    caption: {
      fontSize: '12px',
      fontWeight: 'normal',
      lineHeight: '16px',
    },
    button: {
      fontSize: '16px',
      fontWeight: '600',
      lineHeight: '20px',
    },
  },

  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.22)',
    md: '0 2px 8px rgba(0, 0, 0, 0.25)',
    lg: '0 4px 16px rgba(0, 0, 0, 0.30)',
  },

  // Component specific styles
  components: {
    button: {
      primary: {
        backgroundColor: '#00D4AA',
        borderRadius: '8px',
        padding: '12px 24px',
        color: '#0D1117',
        fontWeight: '600',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
      secondary: {
        backgroundColor: 'transparent',
        border: '1px solid #00D4AA',
        borderRadius: '8px',
        padding: '12px 24px',
        color: '#00D4AA',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
      danger: {
        backgroundColor: '#DA3633',
        borderRadius: '8px',
        padding: '12px 24px',
        color: '#F0F6FC',
        fontWeight: '600',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
    },
    card: {
      backgroundColor: '#161B22',
      borderRadius: '12px',
      padding: '16px',
      margin: '8px 0',
      border: '1px solid #30363D',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)',
    },
    input: {
      backgroundColor: '#21262D',
      border: '1px solid #30363D',
      borderRadius: '8px',
      padding: '12px 16px',
      fontSize: '16px',
      color: '#F0F6FC',
      outline: 'none',
      transition: 'border-color 0.2s ease',
    },
  },
};

// Helper functions for theme usage
export const getTextStyle = (variant: keyof typeof VaultKeTheme.typography) => {
  return {
    ...VaultKeTheme.typography[variant],
    color: VaultKeTheme.colors.text,
  };
};

export const getButtonStyle = (variant: keyof typeof VaultKeTheme.components.button) => {
  return VaultKeTheme.components.button[variant];
};

export const getChamaRoleColor = (role: keyof typeof VaultKeTheme.colors.chamaRole) => {
  return VaultKeTheme.colors.chamaRole[role] || VaultKeTheme.colors.chamaRole.member;
};

export const getStatusColor = (status: string) => {
  switch (status) {
    case 'success':
    case 'completed':
    case 'active':
      return VaultKeTheme.colors.success;
    case 'warning':
    case 'pending':
      return VaultKeTheme.colors.warning;
    case 'error':
    case 'failed':
    case 'cancelled':
      return VaultKeTheme.colors.error;
    case 'info':
    case 'processing':
      return VaultKeTheme.colors.info;
    default:
      return VaultKeTheme.colors.textSecondary;
  }
};